import { AccordionTypes } from '@genial/components';
import { ColorTypes } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import {
  FixedIncomeOfferBaseEntity,
  FundOfferBaseEntity,
  EquityEntity,
  CoeOfferEntity,
  TreasuryOfferBaseEntity,
} from '@genial/services';
import type { FixedIncomePositionEntity } from '@genial/services';
import {
  V1GetAllEquitiesOffersRequest,
  V1GetAllTreasuriesOffersRequest,
  V1GetCoesOffersFilterRequest,
  V1GetEquitiesOffersBySearchTermRequest,
  V1GetEquitiesPositionByAccountNumberRequest,
  V1GetFixedIncomeOffersFilterRequest,
  V1GetFundsOffersFilterRequest,
  type V1GetFixedIncomeRedemptionsByAccountNumberSearchTermRequest,
  type V1GetTreasuriesWithdrawSearchTermRequest,
  V1GetTreasuriesOffersFilterRequest,
  type V1GetFundsRedemptionsAccountNumberSerachTermRequest,
} from '@genial/services/dist/apis';
import {
  FixedIncomeEnumsFixedIncomeProductSubCategoryEnum,
  FundEnumsFundProductCategoryEnum,
  GenialB2bPartnerPortalBFFWebApiDomainInterfacesRequestSortItem,
  IndexerResponseFilter,
  OrderEntityDistributionFee,
  RequestedOfferDetailRequest,
  ResponsesFundOfferDetailsResponse,
  RiskResponseFilter,
  type ResponsesTreasuryWithdrawItemResponse,
  ResponsesRedemptionFundDetailsResponse,
} from '@genial/services/dist/models';

import { FilterModalTypes } from '../../components/FilterModal';
import { TableHeadItemOrderBy } from '../../components/TableHead';
import { FiltersToApplyProps } from '../../hooks/useFilterTransformer';
import { OperationType } from '../../pages/BatchTicket/components/DynamicAsset/contexts/constants';
import { FundWithdrawDateQuotationNormalized } from '../redemptionSolicitation/types';

import { PublicOfferTypes } from './external/constants';
import { PublicOfferModality } from './external/types';

export interface SelectedProduct
  extends Partial<
    FixedIncomeOfferEntityNormalized &
      FixedIncomePositionEntity &
      FundOfferBaseEntity &
      FundRedemptionOfferNormalized &
      EquityEntity &
      CoeOfferEntity &
      ResponsesTreasuryWithdrawItemResponse &
      TreasuryOfferBaseEntity
  > {
  id: keyof AssetSelectedTypeMap;
  operationType?: OperationType;
  orderValue?: number;
  orderQuantity?: number;
  orderType?: 'Limit' | 'Market';
  appliedAmount?: number;
  minimumMovimentation?: number;
  amountProductItemSelected?: number;
  priceProductItemSelected?: number;
  type: 'FixedIncome' | 'Fund' | 'Equity' | 'StructuredOperations' | 'DirectTreasury';
  availableAmount?: number;
  rescueQuantity?: number;
  scheduleDate?: Date;
  dateQuotation?: FundWithdrawDateQuotationNormalized;
  billingTypeId?: string;
  orderFee?: FixedIncomeDistributionFee;
  isTotalRedemption?: boolean;
  estimatedIncomeCalculated?: number | null;
  estimatedNetValueCalculated?: number | null;
}

export interface ProductStatus {
  label: string;
  status: AccordionTypes.Status;
}

export interface ProductItem {
  current: SelectedProduct;
  previous: SelectedProduct;
}

export type FixedIncomeOffersByFilterParams = V1GetFixedIncomeOffersFilterRequest;
export type FixedIncomeProductSubCategoryEnum = FixedIncomeEnumsFixedIncomeProductSubCategoryEnum;
export type FixedIncomeColumnSortBy =
  GenialB2bPartnerPortalBFFWebApiDomainInterfacesRequestSortItem;
export type FixedIncomeOffersBySearchTerm = V1GetFixedIncomeOffersFilterRequest & {
  skipEndpointCall: boolean;
};
export type FixedIncomeRescueOffersBySearchTerm =
  V1GetFixedIncomeRedemptionsByAccountNumberSearchTermRequest & {
    skipEndpointCall: boolean;
  };
export type FixedIncomeDistributionFee = OrderEntityDistributionFee & {
  label: string;
  customerFeeAmountFormatted: string;
  customerPuAmountFormatted: string;
  min: number;
  max: number;
  profitability: string;
  honorariumFee: string;
};
export type FixedIncomeOfferEntityNormalized = FixedIncomeOfferBaseEntity & {
  productIdForTerm: number;
  productDisplayNormalized: string;
  profitabilityFormattedNormalized: string;
  suitabilityProfileNameNormalized: string;
  riskRatingNormalized: string;
  productIdNormalized: string;
  maturityDateNormalized: string;
  dueDateNormalized: string;
  daysToMaturityNormalized: string;
  unitPriceNormalized: string;
  riskRatingLevelNormalized: number;
  inputApplicationAmountNormalized: string;
  captions: IconographyTypes.Option[];
  incomeTaxAmountNormalized: string;
  hasFeesOptions: boolean;
  distributionFeesNormalized: FixedIncomeDistributionFee[];
};

export type FixedIncomeOfferSellEntityNormalized = FixedIncomePositionEntity & {
  irFormatted: string;
  irNormalized: number;
  netValueFormatted: string;
  availableInAccountNormalized: string;
  availableQuantityNormalized: string;
  productIdNormalized: string;
  unitaryPriceNormalized: number;
};

export type FundOfferByFilterParams = V1GetFundsOffersFilterRequest;
export type FundProductCategoryEnum = FundEnumsFundProductCategoryEnum;
export type FundColumnSortBy = GenialB2bPartnerPortalBFFWebApiDomainInterfacesRequestSortItem;
export type FundsOffersBySearchTerm = V1GetFundsOffersFilterRequest & {
  skipEndpointCall: boolean;
};
export type FundOfferEntityNormalized = FundOfferBaseEntity & {
  productIdForTerm: number;
  profitabilityTwelveMonthsNormalized: string;
  fundCnpjNormalized: string;
  productIdNormalized: string;
  riskRatingNormalized: string;
  productCategoryNormalized: string;
  productNameNormalized: string;
  administrationFeeNormalized: string;
  quotationRedemptionTermFormattedNormalized: string;
  riskRatingLevelNormalized: string;
  inputApplicationAmountNormalized: string;
  minimumMovimentationNormalized: number;
  minimumMovimentationFormatted: string;
  isReaplicationNormalized: boolean;
  closedFundNormalized: boolean;
  blocked?: boolean | null;
  captions: IconographyTypes.Option[];
};
export type FundOfferDetailEntityNormalized = ResponsesFundOfferDetailsResponse & {
  initials: string;
  title: string;
  isMarketClosed: boolean;
  marketStatus: string;
  captions: IconographyTypes.Option[];
};
export type FundRedemptionsOffersBySearchTerm =
  V1GetFundsRedemptionsAccountNumberSerachTermRequest & {
    skipEndpointCall: boolean;
  };

export type EquityOffersParams = V1GetAllEquitiesOffersRequest;
export type EquityOffersPositionParams = V1GetEquitiesPositionByAccountNumberRequest;
export type EquityOffersBySearchTerm = V1GetEquitiesOffersBySearchTermRequest & {
  skipEndpointCall: boolean;
};
export type EquityOfferEntityNormalized = EquityEntity & {
  productIdNormalized: string;
  productNameNormalized: string;
  symbolNormalized: string;
  imageNormalized: string;
  lastPriceNormalized: string;
  priceChangeTaxNormalized: string;
  tradeDateNormalized: string;
  askPriceNormalized: string;
  closePriceNormalized: string;
  bidPriceNormalized: string;
  priceNormalized: number;
  priceNormalizedFormatted: string;
  priceChangeTaxValueNormalized: number;
  priceChangeTaxColorNormalized: ColorTypes.Option;
  saleAvailableAmount?: number;
};

export type DirectTreasuryOffersByFilterParams = V1GetTreasuriesOffersFilterRequest;
export type DirectTreasuryOffersParams = V1GetAllTreasuriesOffersRequest;
export type DirectTreasuryOffersBySearchTerm = V1GetTreasuriesOffersFilterRequest & {
  skipEndpointCall: boolean;
};
export type DirectTreasuryRescueOffersBySearchTerm = V1GetTreasuriesWithdrawSearchTermRequest & {
  skipEndpointCall: boolean;
};
export type DirectTreasuryIndexerCategoriesData = { data: IndexerResponseFilter[] };
export type DirectTreasuryRisksCategoriesData = { data: RiskResponseFilter[] };
export type DirectTreasuryOfferEntityNormalized = TreasuryOfferBaseEntity & {
  productIdNormalized: string;
  productNameNormalized: string;
  profitabilityNormalized: string;
  riskRatingLevelNormalized: string;
  maturityDateFormattedNormalized: string;
  settlementDateFormattedNormalized: string;
  settlementPeriodFormattedNormalized: string;
  feesPaymentNormalized: string;
  inputAmountOrderNormalized: string;
  inputApplicationAmountNormalized: string;
  riskRatingNormalized: string;
};
export type DirectTreasurySellOfferEntityNormalized = ResponsesTreasuryWithdrawItemResponse & {
  productNameNormalized: string;
  totalValueNormalized: string;
  quantityAvailableNormalized: number;
  quantityAvailableFormatted: string;
  redemptionValueNormalized: string;
  availableInAccountNormalized: string;
};

export type StructuredOperationsOffersParams = V1GetCoesOffersFilterRequest;
export type StructuredOperationsOffersBySearchTerm = V1GetCoesOffersFilterRequest & {
  skipEndpointCall: boolean;
};

export type StructuredOperationBillingTypeSelected = { label: string; value: string };

interface EmptyVariationStyle {
  hasVariation: false;
}

interface FilledVariationStyle {
  hasVariation: true;
  icon: IconographyTypes.Option;
  color: ColorTypes.Option;
}

export type VariationStyle = EmptyVariationStyle | FilledVariationStyle;

export type CoesOfferEntityNormalized = CoeOfferEntity & {
  productIdNormalized: string;
  productNameNormalized: string;
  strategyNormalized: string;
  riskRatingNormalized: string;
  riskRatingScoreNormalized: number;
  maturityDateNormalized: string;
  additionalInfoNormalized: {
    valueNormalized: string;
    labelNormalized: string;
    variation: VariationStyle;
  }[];
  minimumApplicationNormalized: number;
  maximumApplicationNormalized: number;
};

export type PublicOfferEntityNormalized = {
  publicOfferType: PublicOfferTypes;
  offerId: number;
  offerType: string;
  offerTypeId: string;
  suitabilityProfile: string;
  isQualified: boolean;
  isProfessional: boolean;
  companyName: string;
  offerName: string;
  isinCode: string;
  alterationDateNormalized: number;
  alterationDateFormatted: string;
  negotiationDateNormalized: number;
  negotiationDateFormatted: string;
  liquidityDateNormalized: number;
  liquidityDateFormatted: string;
  limiter: string;
  limiterCode: number;
  deadlineMonths: number;
  minimumRemuneration: number;
  maximumRemuneration: number;
  maximumRemunerationAsReceived?: number;
  minimumEquityValue: number;
  maximumEquityValue: number;
  relationshipIndex: string;
  benchmarkName: string;
  documents: {
    fileName: string;
    fileUrl: string;
    fileAvailable: boolean;
  }[];
  pricingMethod?: string;
  rateLimiterTranslated?: string;
};

export interface PublicOfferDetailsModalityNormalized extends PublicOfferModality {
  modalityName: string;
  modalityDescription: string;
  modalityType: string;
  reservationStartDateFormatted: string;
  reservationEndDateFormatted: string;
  minimumQuantity: number;
  maximumQuantity: number;
  minimumApplication: number;
  maximumApplication: number;
  minimumApplicationFormatted: string;
  maximumApplicationFormatted: string;
  isExpired: boolean;
  linked: boolean;
}

export interface PublicOfferDetailsModalityWithTimelineNormalized
  extends PublicOfferDetailsModalityNormalized {
  timeline: {
    date: Date;
    label: string;
    formattedDate: string;
  }[];
}

export interface PublicOfferDetailsNormalized {
  modalities: PublicOfferDetailsModalityNormalized[];
}

export interface PublicOfferAssetToInvestNormalized {
  rating: string;
  riskRating: string;
  riskRatingCode: number;
  deadlineMonthsFormatted: string;
  issuer: string;
  unitaryPrice: number;
  unitaryPriceFormatted: string;
  valueAlreadyAssignedFormatted: string;
  rules: string;
  linkedModalities: PublicOfferDetailsModalityWithTimelineNormalized[];
  unlinkedModalities: PublicOfferDetailsModalityWithTimelineNormalized[];
}

export type ClubsEntityNormalized = {
  clubId: number;
  fundId: number;
  clientId: number;
  managerNameNormalized: string;
  clubNameNormalized: string;
  minimumApplication: number;
  minimumApplicationNormalized: string;
  minimumMovement: number;
  minimumMovementNormalized: string;
  redemptionQuotationDays: number;
  redemptionSettlementDays: number;
  applicationQuotationDays: number;
  cutoffTime: Date;
  cutoffTimeNormalized: string;
};

export type ClubsDetailsEntityNormalized = {
  clubId: number;
  fundId: number;
  clientId: number;
  clubNameNormalized: string;
  managerNameNormalized: string;
  cutoffTime: Date;
  minimumApplication: number;
  minimumApplicationNormalized: string;
  minimumMovement: number;
  minimumMovementNormalized: string;
  cutoffTimeNormalized: string;
  applicationQuotationDays: number;
  redemptionQuotationDays: number;
  redemptionSettlementDays: number;
  quotationDateNormalized: string;
  quotationDate: Date;
  settlementDateNormalized: string;
  settlementDate: Date;
  riskRatingLevelNormalized: number;
  riskRatingNormalized: string;
};

export type ClubsDateEntityNormalized = {
  quotationDateNormalized: string;
  quotationDate: Date;
  settlementDateNormalized: string;
  settlementDate: Date;
};

export interface DynamicMarketStatusProps {
  isOpen: boolean;
  message: string;
  originalData?: any;
}

export interface DynamicCustomFilterProps {
  fieldName: string;
  label: string;
  options: { label: string; value: string }[];
  extraProps?: FilterModalTypes.FilterType;
}

export interface DynamicCustomFiltersProps {
  filters: DynamicCustomFilterProps[];
}

export type DynamicAssetsQueryCustomerParams = {
  accountNumber: number;
  checkingAccountNumber?: number;
  accountId?: number;
  multipleAccountsIds?: number[];
};

export type DynamicAssetsQueryParams = DynamicAssetsQueryCustomerParams & {
  page?: number;
  document?: string;
  searchTerm?: string;
  filtersToApply: FiltersToApplyProps;
  orderBy?: TableHeadItemOrderBy;
  cacheControl?: number;
  fundsOffersV2Enabled?: boolean;
};

export interface DynamicProductCaptionsGetter {
  qualifiedInvestor?: boolean | null;
  dailyLiquidity?: boolean | null;
  fgcProtected?: boolean | null;
  taxExempt?: boolean | null;
  closedFund?: boolean | null;
  professionalInvestor?: boolean | null;
  blocked?: boolean | null;
}

interface AssetWithValue {
  orderValue: number;
  amount?: number | null;
  requestedPrice?: number;
}

interface AssetWithDistributionFee {
  orderFee?: FixedIncomeDistributionFee | null;
}

interface AssetWithEstimatedValues {
  estimatedIncomeCalculated?: number | null;
  estimatedNetValueCalculated?: number | null;
}

interface AssetWithScheduleDate {
  scheduleDate?: Date | null;
  dateQuotation?: FundWithdrawDateQuotationNormalized;
}

interface AssetWithTotalRedemption {
  isTotalRedemption?: boolean;
}

interface AssetWithBillingTypeId {
  billingTypeId?: string | null;
}

interface AssetWithBasketOfferId {
  basketOfferId?: string | null;
}

interface AssetWithBasketCustomerId {
  basketCustomerOrderId?: string | null;
  basketCustomerOfferId?: string | null;
}

interface AssetWithQuantity {
  orderQuantity: number;
  quantity?: number;
}

interface AssetWithEquityOrderType {
  orderType?: 'Limit' | 'Market';
}

export type AssetSelectedTypeMap = {
  fixedIncome: FixedIncomeOfferEntityNormalized &
    AssetWithValue &
    AssetWithDistributionFee &
    AssetWithEstimatedValues &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  fixedIncomeSell: FixedIncomeOfferSellEntityNormalized &
    AssetWithQuantity &
    AssetWithValue &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  directTreasury: DirectTreasuryOfferEntityNormalized &
    AssetWithValue &
    AssetWithQuantity &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  directTreasurySell: DirectTreasurySellOfferEntityNormalized &
    AssetWithQuantity &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  funds: FundOfferEntityNormalized &
    AssetWithValue &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  fundsSell: FundRedemptionOfferNormalized &
    AssetWithValue &
    AssetWithScheduleDate &
    AssetWithTotalRedemption &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  equity: EquityOfferEntityNormalized &
    AssetWithValue &
    AssetWithQuantity &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId &
    AssetWithEquityOrderType;
  equitySell: EquityOfferEntityNormalized &
    AssetWithValue &
    AssetWithQuantity &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  coes: CoesOfferEntityNormalized &
    AssetWithValue &
    AssetWithBillingTypeId &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  publicOffers: PublicOfferEntityNormalized &
    Partial<AssetWithValue> &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  clubs: ClubsEntityNormalized &
    Partial<AssetWithValue> &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
  privatePension: PrivatePensionEntityNormalized &
    AssetWithBasketOfferId &
    AssetWithBasketCustomerId;
};

export type AssetDefaultOrSelectedTypeMap = {
  fixedIncome: FixedIncomeOfferEntityNormalized &
    Partial<AssetWithValue> &
    Partial<AssetWithDistributionFee>;
  fixedIncomeSell: FixedIncomeOfferSellEntityNormalized & Partial<AssetWithQuantity>;
  directTreasury: DirectTreasuryOfferEntityNormalized & Partial<AssetWithValue & AssetWithQuantity>;
  directTreasurySell: DirectTreasurySellOfferEntityNormalized & Partial<AssetWithQuantity>;
  funds: FundOfferEntityNormalized & Partial<AssetWithValue>;
  fundsSell: FundRedemptionOfferNormalized & Partial<AssetWithValue>;
  equity: EquityOfferEntityNormalized &
    Partial<AssetWithValue & AssetWithQuantity> &
    AssetWithEquityOrderType;
  equitySell: EquityOfferEntityNormalized & Partial<AssetWithValue & AssetWithQuantity>;
  coes: CoesOfferEntityNormalized & Partial<AssetWithValue>;
  publicOffers: PublicOfferEntityNormalized & Partial<AssetWithValue>;
  privatePension: PrivatePensionEntityNormalized;
  clubs: ClubsEntityNormalized & Partial<AssetWithValue>;
};

export interface SubmitUpdateProps {
  amount?: number;
  price?: number;
  requestedOfferDetail?: Partial<RequestedOfferDetailRequest>;
}

export type GenericMarketStatus = {
  isOpen: boolean;
  message: string;
  hasOpeningHours: boolean;
  hasClosingHours: boolean;
  openingHour?: Date;
  openingHourNormalized?: string;
  closingHour?: Date;
  closingHourNormalized?: string;
};

export enum ProductWithMarketStatus {
  COES,
  TreasuryDirect,
  Equity,
  FixedIncome,
}

export type FundRedemptionOfferNormalized = ResponsesRedemptionFundDetailsResponse & {
  portfolioId: number;
  productIdFormatted: string;
  productIdNormalized: string;
  productName: string;
  amountToRedemption: number;
  estimatedIncomeTax: string;
  estimatedNetValue: string;
  quotationDate: string;
  quotationDateFormatted: string;
  liquidationDateFormatted: string;
  closedFundNormalized: boolean;
  quotationAndLiquidationNormalized: string;
  attentionMessage: string;
};

export type PrivatePensionRelatedPartyEntityNormalized = {
  relatedPartyId: number;
  insuranceCompanyId: number;
  name: string;
};

export type PrivatePensionInsuranceEntityNormalized = {
  insuranceId: number;
  insuranceName: string;
  insuranceFlag: number;
  insuranceServiceId: number;
  insuranceCnpj: number;
  insuranceCnpjNormalized: string;
  insuranceCode: string | null;
};

export type PrivatePensionBankEntityNormalized = {
  bankId: number;
  insuranceCompanyId: number;
  name: string;
};

export type PrivatePensionClientBankAccountEntityNormalized = {
  bankAccountId: number;
  bankCode: string;
  bankName: string;
  agencyNumber: string;
  agencyDigit: string;
  accountNumber: string;
  accountDigit: string;
};

export type PrivatePensionEntityNormalized = {
  privatePensionId: number;
  productNameNormalized: string;
  /** Can also be called as "category" */
  productTypeNormalized: string;
  insuranceCompanyId: number;
  productDocument: string;
  monthlyRentability: number;
  monthlyRentabilityNormalized: string;
  annualRentability: number;
  annualRentabilityNormalized: string;
  twelveMonthsRentability: number;
  twelveMonthsRentabilityNormalized: string;
  minimumApplication: number;
  minimumApplicationNormalized: string;
  monthlyMinimumApplication: number;
  monthlyMinimumApplicationNormalized: string;
  managerNameNormalized: string;
  managerImage: string;
  valueCurrentQuotation: number;
  valueCurrentQuotationNormalized: string;
  valueDaily: number;
  valueDailyNormalized: string;
  fundProfileNormalized: string;
  initialDateTime: number;
  initialDateNormalized: string;
  referenceDateTime: number;
  referenceDateNormalized: string;
};

export type PrivatePensionDueDateOption = { value: string; label: string };

export type PrivatePensionInsuranceCompanyParametersEntityNormalized = {
  banks: PrivatePensionBankEntityNormalized[];
  bankValidationSettings: {
    [bankId: number]: {
      [ruleName: string]: { required: boolean; minLength: number; maxLength: number };
    };
  };
  maxBeneficiaries: number;
  boletoDueDates: PrivatePensionDueDateOption[];
  debitDueDatesByBankCode: Record<string, PrivatePensionDueDateOption[]>;
};

export type FundComparatorResultNormalized = {
  indexes: {
    id: string;
    indexName: string;
    date: Date;
    dateNormalized: string;
    value: number;
    valueNormalized: string;
  }[];
  products: {
    id: string;
    productName: string;
    date: Date;
    dateNormalized: string;
    value: number;
    valueNormalized: string;
  }[];
};

export type FundComparatorProductSearchResultNormalized = {
  id: string;
  name: string;
};

export interface PrivatePensionDetailsNormalized {
  documents: {
    fileName: string;
    fileUrl: string;
    fileAvailable: boolean;
    fileImage?: string;
  }[];
}
